// Cookie consent management utility for GDPR compliance

export const COOKIE_CONSENT_KEY = 'cookie-consent';
export const COOKIE_PREFERENCES_KEY = 'cookie-preferences';

// Cookie categories
export const COOKIE_CATEGORIES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
  FUNCTIONAL: 'functional'
};

// Default preferences - only necessary cookies accepted by default
export const DEFAULT_PREFERENCES = {
  [COOKIE_CATEGORIES.NECESSARY]: true,
  [COOKIE_CATEGORIES.ANALYTICS]: false,
  [COOKIE_CATEGORIES.MARKETING]: false,
  [COOKIE_CATEGORIES.FUNCTIONAL]: false,
  timestamp: new Date().toISOString(),
  version: '1.0'
};

/**
 * Get current cookie consent status
 * @returns {Object|null} Consent preferences or null if not set
 */
export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    return consent ? JSON.parse(consent) : null;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
};

/**
 * Set cookie consent preferences
 * @param {Object} preferences - Cookie preferences by category
 */
export const setCookieConsent = (preferences) => {
  try {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    
    // Track cookie consent choices for analytics (before checking consent!)
    // This uses a special tracking method that doesn't require consent
    trackCookieConsentChoice(consentData);
    
    // Trigger a custom event for other parts of the app to listen to
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
      detail: consentData
    }));
    
    return consentData;
  } catch (error) {
    console.error('Error setting cookie consent:', error);
    return null;
  }
};

/**
 * Check if user has given consent for a specific cookie category
 * @param {string} category - Cookie category to check
 * @returns {boolean} True if consent given, false otherwise
 */
export const hasConsentFor = (category) => {
  // Necessary cookies are always allowed
  if (category === COOKIE_CATEGORIES.NECESSARY) return true;
  
  const consent = getCookieConsent();
  if (!consent) return false;
  
  return consent[category] === true;
};

/**
 * Check if user has made any consent choice
 * @returns {boolean} True if consent has been given, false if banner should show
 */
export const hasGivenConsent = () => {
  const consent = getCookieConsent();
  return consent !== null;
};

/**
 * Reset all cookie consent preferences
 */
export const resetCookieConsent = () => {
  try {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
      detail: null
    }));
  } catch (error) {
    console.error('Error resetting cookie consent:', error);
  }
};

/**
 * Accept all cookies
 */
export const acceptAllCookies = () => {
  const allAccepted = {
    [COOKIE_CATEGORIES.NECESSARY]: true,
    [COOKIE_CATEGORIES.ANALYTICS]: true,
    [COOKIE_CATEGORIES.MARKETING]: true,
    [COOKIE_CATEGORIES.FUNCTIONAL]: true
  };
  return setCookieConsent(allAccepted);
};

/**
 * Accept only necessary cookies
 */
export const acceptNecessaryCookies = () => {
  return setCookieConsent(DEFAULT_PREFERENCES);
};

/**
 * Get cookie information for display in settings
 */
export const getCookieInfo = () => {
  return {
    [COOKIE_CATEGORIES.NECESSARY]: {
      name: 'Necessary Cookies',
      description: 'These cookies are essential for the website to function properly. They enable basic functionality like page navigation and access to secure areas.',
      examples: ['Session cookies', 'Security cookies', 'Load balancing cookies'],
      canDisable: false
    },
    [COOKIE_CATEGORIES.ANALYTICS]: {
      name: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
      examples: ['Google Analytics', 'Page view tracking', 'User behavior analysis'],
      canDisable: true
    },
    [COOKIE_CATEGORIES.MARKETING]: {
      name: 'Marketing Cookies',
      description: 'These cookies are used to track visitors across websites to display relevant advertisements and measure campaign effectiveness.',
      examples: ['Advertisement cookies', 'Social media tracking', 'Conversion tracking'],
      canDisable: true
    },
    [COOKIE_CATEGORIES.FUNCTIONAL]: {
      name: 'Functional Cookies',
      description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.',
      examples: ['Language preferences', 'Theme settings', 'Form data'],
      canDisable: true
    }
  };
};

/**
 * Track cookie consent choices for analytics purposes
 * This function sends data to Google Analytics without requiring consent
 * as it's legitimate interest for understanding consent patterns
 * @param {Object} consentData - The consent data to track
 */
const trackCookieConsentChoice = (consentData) => {
  try {
    // Log to our backend for analytics (legitimate interest)
    const backendPayload = {
      consent_data: {
        analytics: consentData[COOKIE_CATEGORIES.ANALYTICS] || false,
        marketing: consentData[COOKIE_CATEGORIES.MARKETING] || false,
        functional: consentData[COOKIE_CATEGORIES.FUNCTIONAL] || false,
      },
      timestamp: consentData.timestamp,
      user_agent: navigator.userAgent.substring(0, 100), // Truncated for privacy
    };

    // Send to our backend cookie consent tracking endpoint
    fetch('/api/cookie-consent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(backendPayload)
    }).catch(error => {
      // Silently fail - don't break the user experience
      console.debug('Cookie consent backend tracking failed:', error);
    });

    // Also send to Google Analytics using Measurement Protocol (without requiring consent)
    // This is allowed under legitimate interest for understanding consent patterns
    fetch('https://www.google-analytics.com/collect', {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        v: '1', // Version
        tid: 'G-F351N4EDYQ', // Your GA tracking ID
        cid: generateClientId(), // Anonymous client ID
        t: 'event', // Hit type
        ec: 'Cookie Consent', // Event category
        ea: consentData[COOKIE_CATEGORIES.ANALYTICS] ? 'Analytics Accepted' : 'Analytics Rejected', // Event action
        el: JSON.stringify({
          analytics: consentData[COOKIE_CATEGORIES.ANALYTICS],
          marketing: consentData[COOKIE_CATEGORIES.MARKETING],
          functional: consentData[COOKIE_CATEGORIES.FUNCTIONAL]
        }), // Event label with choices
        ni: 1 // Non-interaction event
      })
    }).catch(error => {
      // Silently fail - don't break the user experience
      console.debug('Cookie consent GA tracking failed:', error);
    });

  } catch (error) {
    console.debug('Cookie consent tracking error:', error);
  }
};

/**
 * Generate an anonymous client ID for tracking
 * @returns {string} Anonymous client ID
 */
const generateClientId = () => {
  // Generate a random UUID-like string for anonymous tracking
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};